import type { HelpContent, HelpSection } from './content/types';

/**
 * Instant search over the help: title, keywords and text, in English and Hinglish. Every word typed
 * must appear (as the start of a word) in the section; each word also matches its other spellings and
 * its English / Hinglish pair (bhugtan ↔ payment, gaadi ↔ truck…), so people find things the way they
 * say them. Sections add their own words through front-matter `keywords`.
 */
const SAME: string[][] = [
  ['payment', 'payments', 'pay', 'paid', 'bhugtan', 'bhugtaan', 'paisa', 'paise', 'bhejna'],
  ['payout', 'payouts', 'advance', 'balance', 'transfer'],
  ['collection', 'collections', 'collect', 'receive', 'received', 'wasooli', 'vasooli', 'udhaar'],
  ['invoice', 'invoices', 'bill', 'bills', 'billing', 'challan'],
  ['shipment', 'shipments', 'load', 'loads', 'maal', 'order', 'orders', 'trip', 'trips', 'safar'],
  ['truck', 'trucks', 'gaadi', 'gadi', 'vehicle', 'vehicles', 'lorry'],
  ['driver', 'drivers', 'chalak', 'chaalak'],
  ['vendor', 'vendors', 'fleet', 'fleets', 'partner', 'partners', 'transporter', 'transporters'],
  ['shipper', 'shippers', 'customer', 'customers', 'party', 'graahak'],
  ['bid', 'bids', 'quote', 'rate', 'bhaav', 'bhav'],
  ['freight', 'kiraya', 'bhada', 'bhaada', 'price'],
  ['document', 'documents', 'docs', 'kagaz', 'kaagaz', 'papers', 'kyc', 'dastavez'],
  ['pod', 'proof', 'delivery', 'delivered'],
  ['route', 'routes', 'lane', 'lanes', 'rasta', 'raasta'],
  ['approve', 'approval', 'approvals', 'manzoori', 'manjoori'],
  ['cancel', 'cancelled', 'radd'],
  ['stopped', 'stop', 'exception', 'exceptions', 'problem', 'issue', 'dikkat', 'samasya', 'ruka'],
  ['assign', 'assigned', 'claim', 'owner', 'unassigned'],
  ['profit', 'margin', 'kamai', 'munafa'],
  ['tracking', 'track', 'location', 'gps'],
  ['delay', 'late', 'der'],
  ['settings', 'setting', 'config'],
];
const ALIASES = new Map<string, string[]>();
for (const group of SAME) for (const w of group) ALIASES.set(w, group);

export const normalise = (s: string) => s.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase();
const words = (q: string) => normalise(q).split(/[^a-z0-9₹#-]+/).filter((w) => w.length > 0);
const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export interface HelpHit {
  section: HelpSection;
  /** Text around the first match in the body. */
  snippet: string;
  score: number;
}

/** Words to highlight (typed words and their aliases), longest first. */
export function highlightPattern(query: string): RegExp | null {
  const qs = words(query);
  if (!qs.length) return null;
  const all = qs.flatMap((w) => [w, ...(ALIASES.get(w) ?? [])]).map(escapeRe).sort((a, b) => b.length - a.length);
  return new RegExp(`(${all.join('|')})`, 'gi');
}

export function searchHelp(content: HelpContent, query: string): HelpHit[] {
  const qs = words(query);
  if (!qs.length) return [];
  const res = qs.map(
    (w) => new RegExp(`(^|[^a-z0-9])(${[w, ...(ALIASES.get(w) ?? []).filter((x) => x !== w)].map(escapeRe).join('|')})`, 'i'),
  );
  const hits: HelpHit[] = [];
  for (const s of content.sections) {
    const title = normalise(s.title);
    const keys = normalise(s.keywords.join(' '));
    const body = normalise(s.text);
    if (!res.every((re) => re.test(title) || re.test(keys) || re.test(body))) continue;
    let score = 0;
    for (const re of res) score += re.test(title) ? 6 : re.test(keys) ? 4 : 1;
    const at = Math.max(0, res.map((re) => body.search(re)).filter((n) => n >= 0).sort((a, b) => a - b)[0] ?? 0);
    const from = Math.max(0, at - 60);
    const raw = s.text.slice(from, from + 180);
    const snippet = `${from > 0 ? '…' : ''}${from > 0 ? raw.replace(/^\S*\s/, '') : raw}${from + 180 < s.text.length ? '…' : ''}`;
    hits.push({ section: s, snippet, score });
  }
  return hits.sort((a, b) => b.score - a.score);
}
